import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Trash2, LogOut, Home, Flag, CheckCircle, XCircle } from "lucide-react";

export default function AdminPage() {
  const [listings, setListings] = useState([]);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("listings");
  const navigate = useNavigate();
  const adminPassword = localStorage.getItem("adminPassword");

  useEffect(() => {
    if (!adminPassword) {
      navigate("/admin/login");
      return;
    }
    fetchListings();
    fetchReports();
  }, [adminPassword, navigate]);

  const fetchListings = async () => {
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/admin/listings`, {
        headers: {
          "admin-password": adminPassword,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setListings(data);
      } else if (response.status === 403) {
        toast.error("Ugyldig admin-tilgang");
        localStorage.removeItem("adminPassword");
        navigate("/admin/login");
      } else {
        toast.error("Kunne ikke hente annonser");
      }
    } catch (error) {
      console.error("Fetch error:", error);
      toast.error("Noe gikk galt. Prøv igjen.");
    } finally {
      setLoading(false);
    }
  };

  const fetchReports = async () => {
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/admin/reports`, {
        headers: {
          "admin-password": adminPassword,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setReports(data);
      }
    } catch (error) {
      console.error("Fetch reports error:", error);
    }
  };

  const handleUpdateReportStatus = async (reportId, newStatus) => {
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/admin/reports/${reportId}?status=${newStatus}`, {
        method: "PUT",
        headers: {
          "admin-password": adminPassword,
        },
      });

      if (response.ok) {
        toast.success("Rapport oppdatert");
        setReports(reports.map(r => r.id === reportId ? { ...r, status: newStatus } : r));
      } else {
        toast.error("Kunne ikke oppdatere rapport");
      }
    } catch (error) {
      console.error("Update report error:", error);
      toast.error("Noe gikk galt");
    }
  };

  const handleDelete = async (listingId, title) => {
    if (!window.confirm(`Er du sikker på at du vil slette "${title}"?`)) {
      return;
    }

    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/listings/${listingId}`, {
        method: "DELETE",
        headers: {
          "admin-password": adminPassword,
        },
      });

      if (response.ok) {
        toast.success("Annonse slettet");
        setListings(listings.filter((l) => l.id !== listingId));
      } else if (response.status === 403) {
        toast.error("Ugyldig admin-tilgang");
        localStorage.removeItem("adminPassword");
        navigate("/admin/login");
      } else {
        toast.error("Kunne ikke slette annonse");
      }
    } catch (error) {
      console.error("Delete error:", error);
      toast.error("Noe gikk galt. Prøv igjen.");
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("adminPassword");
    toast.success("Logget ut");
    navigate("/");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-lg text-gray-600">Laster...</p>
      </div>
    );
  }

  const pendingReports = reports.filter(r => r.status === "pending");

  const getReasonLabel = (reason) => {
    const labels = {
      inappropriate: "Upassende innhold",
      spam: "Spam eller falsk annonse",
      fraud: "Mulig svindel",
      wrong_category: "Feil kategori",
      other: "Annet"
    };
    return labels[reason] || reason;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto py-8 px-4">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Admin Panel</h1>
            <p className="text-gray-600 mt-1">
              Administrer annonser og rapporter
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => navigate("/")}>
              <Home className="w-4 h-4 mr-2" />
              Hjem
            </Button>
            <Button variant="outline" onClick={handleLogout}>
              <LogOut className="w-4 h-4 mr-2" />
              Logg ut
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <Button
            variant={activeTab === "listings" ? "default" : "outline"}
            onClick={() => setActiveTab("listings")}
          >
            Annonser ({listings.length})
          </Button>
          <Button
            variant={activeTab === "reports" ? "default" : "outline"}
            onClick={() => setActiveTab("reports")}
            className="relative"
          >
            <Flag className="w-4 h-4 mr-2" />
            Rapporter ({reports.length})
            {pendingReports.length > 0 && (
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">
                {pendingReports.length}
              </span>
            )}
          </Button>
        </div>

        {/* Reports Tab */}
        {activeTab === "reports" && (
          <div className="space-y-4">
            {reports.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <p className="text-gray-500">Ingen rapporter</p>
                </CardContent>
              </Card>
            ) : (
              reports.map((report) => (
                <Card key={report.id} className={`hover:shadow-lg transition-shadow ${report.status === "pending" ? "border-l-4 border-l-red-500" : ""}`}>
                  <CardContent className="p-6">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge variant={
                            report.status === "pending" ? "destructive" :
                            report.status === "resolved" ? "default" :
                            report.status === "dismissed" ? "secondary" : "outline"
                          }>
                            {report.status === "pending" ? "Venter" :
                             report.status === "resolved" ? "Løst" :
                             report.status === "dismissed" ? "Avvist" : "Under behandling"}
                          </Badge>
                          <Badge variant="outline">{getReasonLabel(report.reason)}</Badge>
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900">
                          Annonse: {report.listing_title}
                        </h3>
                        {report.description && (
                          <p className="text-gray-600 mt-2">{report.description}</p>
                        )}
                        <p className="text-sm text-gray-400 mt-2">
                          Rapportert: {new Date(report.created_at).toLocaleDateString('nb-NO', {
                            day: 'numeric',
                            month: 'long',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => navigate(`/listing/${report.listing_id}`)}
                        >
                          Se annonse
                        </Button>
                        {report.status === "pending" && (
                          <>
                            <Button
                              size="sm"
                              variant="default"
                              className="bg-green-600 hover:bg-green-700"
                              onClick={() => handleUpdateReportStatus(report.id, "resolved")}
                            >
                              <CheckCircle className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleUpdateReportStatus(report.id, "dismissed")}
                            >
                              <XCircle className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        )}

        {/* Listings Tab */}
        {activeTab === "listings" && (
          listings.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <p className="text-gray-500">Ingen annonser funnet</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {listings.map((listing) => (
              <Card key={listing.id} className="hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="flex gap-4">
                    {listing.images && listing.images.length > 0 && (
                      <img
                        src={listing.images[0]}
                        alt={listing.title}
                        className="w-32 h-32 object-cover rounded-lg"
                      />
                    )}
                    <div className="flex-1">
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="text-xl font-semibold text-gray-900">
                            {listing.title}
                          </h3>
                          <div className="flex gap-2 mt-2">
                            <Badge variant={listing.status === "sold" ? "secondary" : "default"}>
                              {listing.status === "sold" ? "Solgt" : "Aktiv"}
                            </Badge>
                            <Badge variant="outline">{listing.category}</Badge>
                            <Badge variant="outline">{listing.condition}</Badge>
                          </div>
                          <p className="text-sm text-gray-600 mt-2 line-clamp-2">
                            {listing.description}
                          </p>
                          <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
                            <div>
                              <span className="font-medium">Pris:</span> {listing.price} kr
                            </div>
                            <div>
                              <span className="font-medium">Lokasjon:</span> {listing.location}
                            </div>
                            <div>
                              <span className="font-medium">Selger:</span> {listing.seller_name}
                            </div>
                            <div>
                              <span className="font-medium">Visninger:</span> {listing.views || 0}
                            </div>
                          </div>
                        </div>
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => handleDelete(listing.id, listing.title)}
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Slett
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
            </div>
          )
        )}
      </div>
    </div>
  );
}
